import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import LockIcon from "@mui/icons-material/Lock";
import JitsiMeet, { leaveMeetingToPortal } from "./JitsiMeet";
import api from "../api";

const MINIMIZED_WIDTH = 56;

/**
 * Excalidraw collab URL must be `#room=<20-hex-id>,<22-char-key>`.
 * A bare `#room=<roomCode>` is ignored → local browser canvas (previous class drawings stick).
 * Derive a stable id+key from the class room_code so tutor/students share one board per class.
 */
async function deriveExcalidrawRoom(roomCode) {
  const data = new TextEncoder().encode(`11plusmagic-excalidraw-v1:${String(roomCode)}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(digest);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  const roomId = hex.slice(0, 20);
  const keyBytes = bytes.slice(10, 26);
  let binary = "";
  keyBytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  const roomKey = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "").slice(0, 22);
  return { roomId, roomKey };
}

/**
 * Split live class: self-hosted Jitsi (left) + shared Excalidraw whiteboard (right).
 * Same room_code joins the same whiteboard for tutor and students; each class is isolated.
 */
export default function MeetingWithWhiteboard({
  roomCode,
  displayName,
  canRecord = false,
  autoStartRecording = false,
  recordingMode = "local",
  rtmpDestination = null,
  recordingId = null,
  jwt = null,
  isModerator = false,
  drawingEnabled = true,
  isTeacher = false,
  joinError = null,
  loading = false,
  leavePath = "/",
  meetingLabel = "Meeting",
}) {
  const code = roomCode || "";
  const [meetingMinimized, setMeetingMinimized] = useState(false);
  const [whiteboardMinimized, setWhiteboardMinimized] = useState(false);
  const [whiteboardSrc, setWhiteboardSrc] = useState(null);

  const showLockOverlay = !drawingEnabled && !isTeacher;

  useEffect(() => {
    let cancelled = false;
    setWhiteboardSrc(null);
    if (!code) return undefined;

    deriveExcalidrawRoom(code).then(({ roomId, roomKey }) => {
      if (cancelled) return;
      // Unique collab room per class; tutor + students with same room_code share this board.
      setWhiteboardSrc(
        `https://excalidraw.com/#room=${roomId},${roomKey}&class=${encodeURIComponent(code)}`
      );
    });

    return () => {
      cancelled = true;
    };
  }, [code]);

  const onLeave = useCallback(() => {
    const finish = () => leaveMeetingToPortal(leavePath);
    if (recordingId) {
      api.post(`tutor/recordings/${recordingId}/finalize`).catch(() => null).finally(finish);
      return;
    }
    finish();
  }, [leavePath, recordingId]);

  if (loading) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#1a1a2e" }}>
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100vw", height: "100vh", minHeight: 500, overflow: "hidden", display: "flex", flexDirection: "row" }}>
      {/* Left: Meeting */}
      <Box
        sx={{
          position: "relative",
          width: meetingMinimized ? MINIMIZED_WIDTH : "50%",
          flex: meetingMinimized ? "0 0 auto" : 1,
          minWidth: meetingMinimized ? MINIMIZED_WIDTH : 280,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "#1a1a2e",
          transition: "width 0.2s ease, flex 0.2s ease",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: meetingMinimized ? "center" : "space-between",
            px: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {!meetingMinimized && (
            <Typography variant="subtitle2" color="text.secondary">
              {meetingLabel}
              {isModerator ? " · Moderator · Recording" : ""}
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={() => setMeetingMinimized((m) => !m)}
            title={meetingMinimized ? "Expand meeting" : "Minimize meeting"}
            sx={{ color: "text.secondary" }}
          >
            {meetingMinimized ? <UnfoldMoreRoundedIcon /> : <UnfoldLessRoundedIcon />}
          </IconButton>
        </Box>
        {joinError && !meetingMinimized && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {joinError}
          </Alert>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            ...(meetingMinimized && {
              position: "absolute",
              left: -10000,
              top: 40,
              width: 640,
              height: 480,
              flex: "none",
            }),
          }}
        >
          {code && (
            <JitsiMeet
              roomCode={code}
              displayName={displayName}
              height="100%"
              canRecord={canRecord}
              autoStartRecording={autoStartRecording}
              recordingMode={recordingMode}
              rtmpDestination={rtmpDestination}
              jwt={jwt}
              soloWithTutor={!isTeacher}
              onLeave={onLeave}
            />
          )}
        </Box>
      </Box>

      {/* Right: Whiteboard — isolated Excalidraw collab room per class room_code */}
      {code && (
        <Box
          key={`whiteboard-${code}`}
          sx={{
            width: whiteboardMinimized ? MINIMIZED_WIDTH : "50%",
            flex: whiteboardMinimized ? "0 0 auto" : 1,
            minWidth: whiteboardMinimized ? MINIMIZED_WIDTH : 280,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
            transition: "width 0.2s ease, flex 0.2s ease",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: whiteboardMinimized ? "center" : "space-between",
              px: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {!whiteboardMinimized && (
              <Typography variant="subtitle2" color="text.secondary">
                Whiteboard
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => setWhiteboardMinimized((w) => !w)}
              title={whiteboardMinimized ? "Expand whiteboard" : "Minimize whiteboard"}
              sx={{ color: "text.secondary" }}
            >
              {whiteboardMinimized ? <UnfoldMoreRoundedIcon /> : <UnfoldLessRoundedIcon />}
            </IconButton>
          </Box>
          {!whiteboardMinimized && (
            <Box
              className="whiteboard-wrapper"
              sx={{
                flex: 1,
                minHeight: 0,
                p: 1.5,
                boxSizing: "border-box",
                position: "relative",
              }}
            >
              {whiteboardSrc ? (
                <iframe
                  id="board"
                  key={whiteboardSrc}
                  src={whiteboardSrc}
                  title="Excalidraw Whiteboard"
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 300,
                    border: "none",
                    borderRadius: 12,
                    display: "block",
                  }}
                />
              ) : (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {showLockOverlay && (
                <Box
                  className="lock-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    m: 1.5,
                    borderRadius: 12,
                    bgcolor: "rgba(0,0,0,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 600,
                    gap: 1,
                  }}
                >
                  <LockIcon />
                  Teacher has locked drawing
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
